/**
 * Discovery Center Cache Utility
 *
 * Fetches all discovery centers once and caches them using @fs/zion-cache
 * to avoid repeated API calls. This replaces the old centers.json approach
 * with a dynamic, cacheable API solution.
 */

import Cache from '@fs/zion-cache'
import { getDiscoveryCenterData } from './services'

const CACHE_KEY = 'discoveryCentersCache'
const CACHE_DURATION_HOURS = 24

// Initialize cache with sessionStorage and 24-hour expiration
const cache = new Cache({
  storeName: 'ds_apps',
  type: 'session',
})

/**
 * Clear the centers cache
 */
export const clearCentersCache = () => {
  cache.removeItem(CACHE_KEY)
}

/**
 * Get all discovery centers (cached or fresh from API)
 * @returns {Promise<Object>} Object with centers keyed by lowercase code
 *
 * Example return value:
 * {
 *   "fhl": { id: 1, code: "FHL", name: "Family History Library", latitude: 40.77, longitude: -111.89 },
 *   "leh": { id: 2, code: "LEH", name: "Lehi Center", latitude: 40.42, longitude: -111.90 },
 *   ...
 * }
 */
export const getAllCenters = async () => {
  // Try cache first
  const cached = await cache.getItem(CACHE_KEY)
  if (cached) {
    return cached
  }

  // Cache miss - fetch from API
  try {
    const centersArray = await getDiscoveryCenterData()

    // Convert array to object keyed by lowercase code for easy lookup
    const centersMap = centersArray.reduce((acc, center) => {
      acc[center.code.toLowerCase()] = center
      return acc
    }, {})

    // Cache with expiration
    await cache.setItem(CACHE_KEY, centersMap, { expiresIn: CACHE_DURATION_HOURS * 60 * 60 })

    return centersMap
  } catch (error) {
    console.error('Failed to fetch discovery centers:', error)
    // Cache empty result to prevent repeated failures
    const emptyMap = {}
    await cache.setItem(CACHE_KEY, emptyMap, { expiresIn: CACHE_DURATION_HOURS * 60 * 60 })
    return emptyMap
  }
}

/**
 * Get a single center by code (from cache if available)
 * @param {string} centerCode - Center code like "FHL", "LEH", etc.
 * @returns {Promise<Object|null>} Center object or null if not found
 */
export const getCenterByCode = async (centerCode) => {
  if (!centerCode) return null
  const centers = await getAllCenters()
  return centers[centerCode.toLowerCase()] || null
}

/**
 * Check if a center code is valid
 * @param {string} centerCode - Center code to validate
 * @returns {Promise<boolean>} True if center exists
 */
export const isValidCenterCode = async (centerCode) => {
  if (!centerCode) return false
  const centers = await getAllCenters()
  return centerCode.toLowerCase() in centers
}

/**
 * Get all valid center codes
 * @returns {Promise<string[]>} Array of lowercase center codes
 */
export const getValidCenterCodes = async () => {
  const centers = await getAllCenters()
  return Object.keys(centers)
}
